/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// MEMORY MONITORING & LEAK DETECTION
// ============================================================================

import { clearTokenCache } from "@/lib/auth/token-utils";
import { logger } from "@/lib/utils/logger";

// ============================================================================
// MEMORY THRESHOLDS
// ============================================================================

const MEMORY_THRESHOLDS = {
  WARNING: 0.7, // 70% - warn when memory usage exceeds this (reduced from 75%)
  CRITICAL: 0.8, // 80% - critical alert when memory usage exceeds this (triggers cleanup) (reduced from 85%)
  MAX: 0.8, // 80% - maximum before forced cleanup (reduced from 85% to prevent GC thrashing at 98%)
} as const;

// ============================================================================
// MEMORY MONITORING
// ============================================================================

export interface MemorySnapshot {
  timestamp: number;
  heapUsed: number;
  heapTotal: number;
  external: number;
  rss?: number;
  usagePercent: number;
}

// HMR-safe: prevent duplicate monitoring intervals on hot reload
declare global {
  // eslint-disable-next-line no-var
  var __cc_memMonitorInterval: NodeJS.Timeout | null | undefined;
  // eslint-disable-next-line no-var
  var __cc_memLastSnapshot: MemorySnapshot | null | undefined;
}
let memoryMonitoringInterval = globalThis.__cc_memMonitorInterval ?? null;
let lastSnapshot: MemorySnapshot | null =
  globalThis.__cc_memLastSnapshot ?? null;
let alertCooldown = 0;

/**
 * Get current memory usage snapshot
 */
export function getMemorySnapshot(): MemorySnapshot | null {
  if (typeof window === "undefined") {
    // Server-side: use Node.js memory API
    const usage = process.memoryUsage();
    const usagePercent = usage.heapUsed / usage.heapTotal;

    return {
      timestamp: Date.now(),
      heapUsed: usage.heapUsed,
      heapTotal: usage.heapTotal,
      external: usage.external,
      rss: usage.rss,
      usagePercent,
    };
  } else {
    // Client-side: use Performance API if available
    const perf = performance as {
      memory?: {
        usedJSHeapSize: number;
        totalJSHeapSize: number;
        jsHeapSizeLimit: number;
      };
    };

    if (perf.memory) {
      const usagePercent =
        perf.memory.usedJSHeapSize / perf.memory.jsHeapSizeLimit;

      return {
        timestamp: Date.now(),
        heapUsed: perf.memory.usedJSHeapSize,
        heapTotal: perf.memory.totalJSHeapSize,
        external: 0,
        usagePercent,
      };
    }
  }

  return null;
}

/**
 * Check memory usage and log alerts if thresholds are exceeded
 */
export function checkMemoryUsage(): void {
  const snapshot = getMemorySnapshot();
  if (!snapshot) return;

  lastSnapshot = snapshot;

  // Prevent alert spam (cooldown of 60 seconds)
  const now = Date.now();
  if (alertCooldown > now) return;

  if (snapshot.usagePercent >= MEMORY_THRESHOLDS.MAX) {
    logger.error("[MEMORY] CRITICAL: Memory usage at maximum threshold", {
      usagePercent: (snapshot.usagePercent * 100).toFixed(2) + "%",
      heapUsed: formatBytes(snapshot.heapUsed),
      heapTotal: formatBytes(snapshot.heapTotal),
    });
    alertCooldown = now + 60000; // 60 second cooldown
    triggerMemoryCleanup();
  } else if (snapshot.usagePercent >= MEMORY_THRESHOLDS.CRITICAL) {
    logger.warn("[MEMORY] CRITICAL: Memory usage exceeds critical threshold", {
      usagePercent: (snapshot.usagePercent * 100).toFixed(2) + "%",
      heapUsed: formatBytes(snapshot.heapUsed),
      heapTotal: formatBytes(snapshot.heapTotal),
    });
    alertCooldown = now + 60000; // 60 second cooldown
    triggerMemoryCleanup();
  } else if (snapshot.usagePercent >= MEMORY_THRESHOLDS.WARNING) {
    logger.warn("[MEMORY] WARNING: Memory usage exceeds warning threshold", {
      usagePercent: (snapshot.usagePercent * 100).toFixed(2) + "%",
      heapUsed: formatBytes(snapshot.heapUsed),
      heapTotal: formatBytes(snapshot.heapTotal),
    });
    alertCooldown = now + 120000; // 2 minute cooldown for warnings
  }
}

/**
 * Trigger aggressive memory cleanup
 * PERFORMANCE: Enhanced cleanup to prevent 98% memory usage
 */
function triggerMemoryCleanup(): void {
  logger.warn("[MEMORY] Starting aggressive memory cleanup");

  // Force garbage collection if available (Node.js with --expose-gc)
  if (typeof global !== "undefined" && (global as { gc?: () => void }).gc) {
    try {
      (global as { gc: () => void }).gc();
      logger.info("[MEMORY] Forced garbage collection");
    } catch (error) {
      // GC not available or failed
    }
  }

  // Clear token cache
  try {
    clearTokenCache();
  } catch (error) {
    // Token cache clear failed, continue
  }

  // Clear ALL in-memory caches — Redis still has the data
  if (typeof window === "undefined") {
    try {
      // 1. Main memory cache (unified-cache-stores)
      const {
        memoryCache,
        cacheMetrics,
        tagManager,
      } = require("@/lib/cache/unified-cache-stores");
      memoryCache.clear();
      cacheMetrics.reset();
      tagManager.clear();

      // 2. Simple cache / MultiLayerCache L1
      const { cache } = require("@/lib/cache/simple-cache");
      cache.clear();

      // 3. Query cache registry
      const { queryCacheManager } = require("@/lib/cache/query-cache");
      if (queryCacheManager && queryCacheManager.getStats) {
        // Clear warming queue and query registry via invalidation
        queryCacheManager.clearRegistry?.();
      }

      logger.info(
        "[MEMORY] Cleared all in-memory caches (Redis L2 still intact)",
      );
    } catch (error) {
      logger.warn("[MEMORY] Cache cleanup partially failed", {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  // Check memory after cleanup
  const snapshot = getMemorySnapshot();
  if (snapshot) {
    logger.info("[MEMORY] Cleanup complete", {
      usagePercent: (snapshot.usagePercent * 100).toFixed(2) + "%",
      heapUsed: formatBytes(snapshot.heapUsed),
      heapTotal: formatBytes(snapshot.heapTotal),
    });
  }
}

/**
 * Start memory monitoring
 * Idempotent: safe to call multiple times, will only start once
 */
export function startMemoryMonitoring(intervalMs: number = 30000): void {
  // OPTIMIZATION: If already monitoring, don't restart (prevents duplicate intervals)
  if (memoryMonitoringInterval) {
    logger.debug("[MEMORY] Monitoring already started, skipping", {
      intervalMs,
    });
    return;
  }

  // Initial check
  checkMemoryUsage();

  // Set up periodic monitoring — store on globalThis for HMR safety
  memoryMonitoringInterval = setInterval(() => {
    checkMemoryUsage();
  }, intervalMs);
  globalThis.__cc_memMonitorInterval = memoryMonitoringInterval;

  logger.info("[MEMORY] Memory monitoring started", {
    intervalMs,
    thresholds: {
      warning: (MEMORY_THRESHOLDS.WARNING * 100).toFixed(0) + "%",
      critical: (MEMORY_THRESHOLDS.CRITICAL * 100).toFixed(0) + "%",
      max: (MEMORY_THRESHOLDS.MAX * 100).toFixed(0) + "%",
    },
  });
}

/**
 * Stop memory monitoring
 */
export function stopMemoryMonitoring(): void {
  if (memoryMonitoringInterval) {
    clearInterval(memoryMonitoringInterval);
    memoryMonitoringInterval = null;
    globalThis.__cc_memMonitorInterval = null;
    logger.info("[MEMORY] Memory monitoring stopped");
  }
}

/**
 * Get last memory snapshot
 */
export function getLastMemorySnapshot(): MemorySnapshot | null {
  return lastSnapshot;
}

/**
 * Format bytes to human-readable string
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
}

// ============================================================================
// REACT QUERY CACHE MONITORING
// ============================================================================

/**
 * Monitor React Query cache size
 */
export function monitorReactQueryCache(queryClient: {
  getQueryCache: () => { getAll: () => unknown[] };
  getMutationCache: () => { getAll: () => unknown[] };
}): {
  queryCount: number;
  mutationCount: number;
  cacheSize: number; // Estimated size in bytes
} {
  const queryCache = queryClient.getQueryCache();
  const mutationCache = queryClient.getMutationCache();

  const queries = queryCache.getAll();
  const mutations = mutationCache.getAll();

  // Estimate cache size (rough calculation)
  const cacheSize = queries.length * 1024 + mutations.length * 512; // Rough estimate

  return {
    queryCount: queries.length,
    mutationCount: mutations.length,
    cacheSize,
  };
}

/**
 * Check React Query cache and log if it's too large
 */
export function checkReactQueryCache(queryClient: {
  getQueryCache: () => { getAll: () => unknown[] };
  getMutationCache: () => { getAll: () => unknown[] };
}): void {
  const stats = monitorReactQueryCache(queryClient);

  // Warn if cache has too many entries
  if (stats.queryCount > 500) {
    logger.warn("[REACT QUERY] Cache has many queries", {
      queryCount: stats.queryCount,
      mutationCount: stats.mutationCount,
      estimatedSize: formatBytes(stats.cacheSize),
    });
  }

  // Critical if cache has excessive entries
  if (stats.queryCount > 1000) {
    logger.error(
      "[REACT QUERY] Cache has excessive queries - potential memory leak",
      {
        queryCount: stats.queryCount,
        mutationCount: stats.mutationCount,
        estimatedSize: formatBytes(stats.cacheSize),
      },
    );
  }
}
